import React from "react";
import { Card, Col, Row } from "react-bootstrap";
import { IItemRanking } from "./ListaRanking";

interface IEstadoLista {
  carregando: boolean;
  erro: string | null;
  itens: IItemRanking[] | null;
}

interface IDestaquesFeedback {
  legendaFiltro: string;
  receberam: IEstadoLista;
  forneceram: IEstadoLista;
  /** Conteúdo exibido abaixo dos destaques, dentro do mesmo card (o resumo do período). */
  children?: React.ReactNode;
}

/** Quem está em 1º lugar; em caso de empate, todos os empatados em ordem alfabética. */
const primeirosColocados = (itens: IItemRanking[]) => {
  const maior = Math.max(...itens.map((item) => item.quantidade));
  return itens
    .filter((item) => item.quantidade === maior)
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
};

interface IDestaque extends IEstadoLista {
  titulo: string;
  icone: string;
  cor: string;
  /** RGB do fundo e da borda translúcidos, as mesmas cores do dashboard em dev. */
  rgb: string;
  rotuloQuantidade: string;
}

const Destaque: React.FC<IDestaque> = ({
  titulo,
  icone,
  cor,
  rgb,
  rotuloQuantidade,
  carregando,
  erro,
  itens,
}) => {
  // Todos os estados (com dados, vazio, carregando, erro) usam o mesmo layout
  // (ícone + título + linha principal + linha secundária), para o card não
  // mudar de altura quando os filtros trocam.
  const conteudo = () => {
    if (carregando && !itens) {
      return { principal: "Carregando...", secundaria: "\u00a0", classePrincipal: "text-muted" };
    }
    if (erro) {
      return { principal: erro, secundaria: "Tente novamente mais tarde.", classePrincipal: "text-danger" };
    }
    if (!itens?.length) {
      return {
        principal: "Nenhum feedback",
        secundaria: "no período e tipo selecionados",
        classePrincipal: "text-muted",
      };
    }
    const primeiros = primeirosColocados(itens);
    return {
      principal: primeiros.map((p) => p.nome).join(", "),
      secundaria:
        `${primeiros[0].quantidade} ${rotuloQuantidade}` +
        (primeiros.length > 1 ? ` · empate entre ${primeiros.length}` : ""),
      classePrincipal: "text-dark",
    };
  };

  const renderizarConteudo = () => {
    const { principal, secundaria, classePrincipal } = conteudo();
    return (
      <div className="d-flex align-items-center">
        <div className="me-3">
          <div
            className={`bg-${cor} rounded-circle d-flex align-items-center justify-content-center`}
            style={{ width: "48px", height: "48px" }}
          >
            <i className={`${icone} text-white`} style={{ fontSize: "20px" }}></i>
          </div>
        </div>
        <div className="flex-grow-1 overflow-hidden">
          <h6 className={`mb-1 text-${cor} fw-bold`}>{titulo}</h6>
          <p className={`mb-0 fw-medium text-truncate ${classePrincipal}`} title={principal}>
            {principal}
          </p>
          <small className="text-muted d-block text-truncate" title={secundaria}>
            {secundaria}
          </small>
        </div>
      </div>
    );
  };

  return (
    <div
      className="flex-fill p-3 rounded d-flex flex-column justify-content-center"
      style={{
        background: `rgba(${rgb}, 0.1)`,
        border: `1px solid rgba(${rgb}, 0.2)`,
      }}
      aria-busy={carregando}
    >
      <div style={{ opacity: carregando && itens ? 0.5 : 1, transition: "opacity 0.2s" }}>
        {renderizarConteudo()}
      </div>
    </div>
  );
};

const DestaquesFeedback: React.FC<IDestaquesFeedback> = ({
  legendaFiltro,
  receberam,
  forneceram,
  children,
}) => (
  <Card
    className="h-100 border-0 shadow-sm"
    style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
  >
    <Card.Header className="border-0 bg-transparent pb-0">
      <h4 className="mb-1 text-dark fw-bold">
        <span className="me-2">🏆</span>
        Nossos Campeões de Feedback
      </h4>
      <p className="text-muted mb-0 small">
        Reconhecimento dos colaboradores mais engajados
      </p>
      <small className="text-muted d-block">{legendaFiltro}</small>
    </Card.Header>
    <Card.Body className="pt-3">
      <Row className="g-4">
        {/* Como em dev: destaques empilhados à esquerda e o resumo à direita. */}
        <Col lg={children ? 5 : 12} className="d-flex flex-column gap-3">
          <Destaque
            titulo="Maior Fornecedor"
            icone="mdi mdi-trophy"
            cor="primary"
            rgb="139, 92, 246"
            rotuloQuantidade="feedbacks fornecidos"
            {...forneceram}
          />
          <Destaque
            titulo="Maior Receptor"
            icone="mdi mdi-star"
            cor="info"
            rgb="6, 182, 212"
            rotuloQuantidade="feedbacks recebidos"
            {...receberam}
          />
        </Col>
        {children && <Col lg={7}>{children}</Col>}
      </Row>
    </Card.Body>
  </Card>
);

export default DestaquesFeedback;
